import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { Select } from "components/ui/select/Select"
import { TextArea } from "components/ui/inputs/TextArea"
import { useState, useEffect } from "react"
import { useBackendChatHistoryStore, useChatStore, useSeriesStore } from "store/aiAgent.store"
import { useAskSpecificQuestion, useSendFeedback } from "hooks/api/aiAgent/aiAgent.hook"
import { useSearchParams } from "react-router-dom"
import { Skeleton } from "components/ui/skeleton/Skeleton"
import type { IAvailableSeriesData, TPredefinedQuestions } from "types/aiAgent.type"
import style from "./SeriesSelector.module.scss"

const { seriesSelector, wrapper, helperText, select, feedbackTextArea, feedbackToggle } = style

type Props = {
	series: IAvailableSeriesData | null
	predefinedQuestions: TPredefinedQuestions | undefined
	subject: string
	langsmithTraceId: string | undefined
	answerIndex: number
}

export const SeriesSelector = ({ series, predefinedQuestions, subject, langsmithTraceId, answerIndex }: Props) => {
	const { mutate: sendFeedbackMutation } = useSendFeedback()
	const { backendChatHistory, addBackendChatMessage } = useBackendChatHistoryStore()
	const { getExtractedSeries, setSelectedSeries, selectedSeries, isSeriesSubmitted, setSubmittedSeries } =
		useSeriesStore()
	const { addQuestion, setFailedAnswer, updateLastQuestionTraceId } = useChatStore()
	const { mutate: askSpecificQuestionMutation, isPending: askSpecificQuestionPending } = useAskSpecificQuestion()
	const [searchParams] = useSearchParams()
	const drawingId = Number(searchParams.get("drawingID"))

	const seriesSubmitted = isSeriesSubmitted(answerIndex, selectedSeries)

	const [seriesOptions, setSeriesOptions] = useState<Array<{ label: string; value: string }>>([])
	const [seriesChanged, setSeriesChanged] = useState(false)
	const [userFeedback, setUserFeedback] = useState("")
	const [isFeedbackVisible, setIsFeedbackVisible] = useState(false)

	useEffect(() => {
		if (series) {
			const options = Object.keys(series).map((key) => ({
				label: key,
				value: key,
			}))
			setSeriesOptions(options)
		}
	}, [series])

	useEffect(() => {
		const extractedSeries = getExtractedSeries()
		if (extractedSeries) {
			setSelectedSeries(extractedSeries)
		}
	}, [getExtractedSeries, setSelectedSeries])

	const onChangeSelectHandle = (value: string | number) => {
		const newValue = String(value)
		setSelectedSeries(newValue)

		// Check if series was changed from extracted value
		const extractedSeries = getExtractedSeries()
		if (extractedSeries !== newValue) {
			setSeriesChanged(true)
			setIsFeedbackVisible(true)
		} else {
			setSeriesChanged(false)
			setUserFeedback("")
			setIsFeedbackVisible(false)
		}
	}

	// Create series correction conversation messages
	const createSeriesCorrectionMessages = () => {
		const seriesOptions = Object.keys(series || {})
		const seriesListText = seriesOptions.map((option) => `   ${option}`).join("\n")

		return [
			{ role: "assistant", content: "Is the series selected correctly?" },
			{ role: "user", content: "The series selected incorrectly." },
			{ role: "assistant", content: `Please select the correct series from the list below:\n${seriesListText}` },
			{ role: "user", content: `The correct series is ${selectedSeries}` },
			{
				role: "assistant",
				content: `Perfect, I will consider the series ${selectedSeries} as correct for this document.`,
			},
		]
	}

	// Check if the selected series matches the extracted series
	const checkBidRequestSeries = () => {
		const extractedSeries = getExtractedSeries()
		const drawingUrl = window.location.href

		if (extractedSeries === selectedSeries) {
			sendFeedbackMutation({ drawingId, langsmithTraceId, type: "POSITIVE", drawingUrl })
		} else {
			let feedbackText = `The LLM selected a wrong series ${extractedSeries} for the document.\nThe correct series is ${selectedSeries}`
			if (userFeedback.trim()) {
				feedbackText += `\nUser explanation: ${userFeedback}`
			}
			sendFeedbackMutation({
				drawingId,
				langsmithTraceId,
				type: "NEGATIVE",
				feedback: feedbackText,
				drawingUrl,
				manuallySubmittedByUser: false,
			})
		}
	}

	const bidRequestCreationHandle = () => {
		const bidRequestQuestion = predefinedQuestions?.questions.find((question) => question.type === "bid_request")
		if (bidRequestQuestion) {
			// Start with current backend chat history
			let messagesForAPI = [...backendChatHistory]

			// Add series correction conversation if needed
			const extractedSeries = getExtractedSeries()
			if (extractedSeries !== selectedSeries) {
				const correctionMessages = createSeriesCorrectionMessages()

				// Add correction messages to both store and API messages
				correctionMessages.forEach((message) => {
					addBackendChatMessage(message)
				})
				messagesForAPI = [...messagesForAPI, ...correctionMessages]
			} else {
				// Add series confirmation conversation
				const confirmationMessages = [
					{ role: "assistant", content: "Is the series selected correctly?" },
					{ role: "user", content: "The series selected correctly." },
					{
						role: "assistant",
						content: `Perfect, I will consider the series as a correct for this document.`,
					},
				]

				confirmationMessages.forEach((message) => {
					addBackendChatMessage(message)
				})
				messagesForAPI = [...messagesForAPI, ...confirmationMessages]
			}

			// Send feedback
			checkBidRequestSeries()

			// Create the new message
			const newMessage = { role: "user", content: "Proceed to Bid Request creation" }

			// Add to store
			addBackendChatMessage(newMessage)

			// Add to API messages
			messagesForAPI = [...messagesForAPI, newMessage]

			askSpecificQuestionMutation(
				{
					drawingId,
					questionId: bidRequestQuestion.id,
					type: bidRequestQuestion.type,
					subject,
					messages: messagesForAPI,
					userInput: { series: String(selectedSeries) },
				},
				{
					onSuccess: (response) => {
						addQuestion("Proceed to Bid Request creation", "system")
						updateLastQuestionTraceId(response.data.langsmith_trace_id)
						setSubmittedSeries(answerIndex, selectedSeries)
					},
					onError: () => {
						setFailedAnswer()
					},
				},
			)
		}
	}

	if (askSpecificQuestionPending) {
		return <Skeleton rows={2} />
	}

	return (
		<div className={seriesSelector}>
			<div className={wrapper}>
				<span className={helperText}>Series to be used in the Bid Request</span>
				<Select
					disabled={seriesSubmitted}
					variant="outlined"
					value={selectedSeries}
					onChange={onChangeSelectHandle}
					options={seriesOptions}
					className={select}
				/>
				{seriesChanged && (
					<>
						{isFeedbackVisible ? (
							<TextArea
								className={feedbackTextArea}
								placeholder="Share your feedback (optional)"
								value={userFeedback}
								onChange={(e) => setUserFeedback(e.target.value)}
								onBlur={() => setIsFeedbackVisible(false)}
								minRows={3}
							/>
						) : null}
						<span className={feedbackToggle} onClick={() => setIsFeedbackVisible(!isFeedbackVisible)}>
							Show feedback
						</span>
					</>
				)}
			</div>
			{!seriesSubmitted ? (
				<ButtonPrimary onClick={() => bidRequestCreationHandle()}>Proceed to Bid Request creation</ButtonPrimary>
			) : null}
		</div>
	)
}
